import React from 'react';
import { Form, Alert } from 'react-bootstrap';
import { Building } from 'lucide-react';
import { toast } from 'sonner';

import axiosInstance from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import OnboardingNavigationBar from '../OnboardingNavigationBar';

const CompanyDetailsStep = ({
    companyId,
    companyDetails,
    setCompanyDetails,
    onNext,
    onBack,
    saving,
    setSaving,
    markStepCompleted,
    alreadyCompleted,
}) => {
    const handleChange = (field, value) => {
        setCompanyDetails(prev => ({ ...prev, [field]: value }));
    };

    const isValid = () => {
        return companyDetails.contactPerson.trim() &&
               companyDetails.companyEmail.trim() &&
               companyDetails.companyPhone.trim();
    };

    const handleSave = async () => {
        if (!isValid()) {
            toast.error('Fyll i alla obligatoriska fält');
            return;
        }

        try {
            setSaving(true);
            const url = getApiUrl(`${companyId}/companydetails`);

            // Fetch existing details first — the PUT endpoint expects camelCase
            // keys but GET returns snake_case, so we map them
            const existing = await axiosInstance.get(url);
            const d = existing.data;
            const merged = {
                companyType: d.company_type,
                registrationNumber: d.registration_number,
                vatNumber: d.vat_number,
                address: d.address,
                city: d.city,
                country: d.country,
                postalCode: d.postal_code,
                contactEmail: d.contact_email,
                contactPhone: d.contact_phone,
                contactPerson: companyDetails.contactPerson,
                companyEmail: companyDetails.companyEmail,
                companyPhone: companyDetails.companyPhone,
                plusgirot: d.plusgirot,
                bankgirot: d.bankgirot,
            };

            await axiosInstance.put(url, merged);
            toast.success('Företagsuppgifter sparade');
            markStepCompleted();
            onNext();
        } catch (error) {
            console.error('Error saving company details:', error);
            toast.error('Kunde inte spara företagsuppgifter');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <style>{`
                .company-details-step {
                    max-width: 500px;
                    margin: 0 auto;
                }
                .step-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 0.5rem;
                }
                .step-header-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    background: var(--rgba-primary-1);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: var(--primary);
                }
                .step-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                }
                .step-description {
                    font-size: 0.875rem;
                    color: #64748b;
                    margin-bottom: 1.5rem;
                }
                .onboarding-form-group {
                    margin-bottom: 1.25rem;
                }
                .onboarding-form-label {
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.375rem;
                    display: block;
                }
                .onboarding-form-label .required {
                    color: #ef4444;
                    margin-left: 2px;
                }
                .onboarding-form-input {
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0.625rem 0.875rem;
                    font-size: 0.875rem;
                    width: 100%;
                    transition: border-color 0.15s;
                }
                .onboarding-form-input:focus {
                    border-color: var(--primary);
                    box-shadow: 0 0 0 3px var(--rgba-primary-1);
                    outline: none;
                }
            `}</style>
            <div className="company-details-step">
                <div className="step-header">
                    <div className="step-header-icon">
                        <Building size={20} />
                    </div>
                    <h3 className="step-title">Företagsuppgifter</h3>
                </div>
                <p className="step-description">
                    Lägg till kontaktuppgifter för ditt företag. Dessa används på fakturor och i kommunikation.
                </p>

                {alreadyCompleted && (
                    <Alert variant="success" style={{ fontSize: '0.85rem', borderRadius: '8px', marginBottom: '1.25rem' }}>
                        Företagsuppgifter är redan ifyllda. Du kan uppdatera dem eller gå vidare.
                    </Alert>
                )}

                <div className="onboarding-form-group">
                    <label className="onboarding-form-label">
                        Kontaktperson <span className="required">*</span>
                    </label>
                    <input
                        className="onboarding-form-input"
                        type="text"
                        placeholder="Förnamn Efternamn"
                        value={companyDetails.contactPerson}
                        onChange={(e) => handleChange('contactPerson', e.target.value)}
                    />
                </div>

                <div className="onboarding-form-group">
                    <label className="onboarding-form-label">
                        E-postadress <span className="required">*</span>
                    </label>
                    <input
                        className="onboarding-form-input"
                        type="email"
                        placeholder="foretag@exempel.se"
                        value={companyDetails.companyEmail}
                        onChange={(e) => handleChange('companyEmail', e.target.value)}
                    />
                </div>

                <div className="onboarding-form-group">
                    <label className="onboarding-form-label">
                        Telefonnummer <span className="required">*</span>
                    </label>
                    <input
                        className="onboarding-form-input"
                        type="tel"
                        placeholder="070-123 45 67"
                        value={companyDetails.companyPhone}
                        onChange={(e) => handleChange('companyPhone', e.target.value)}
                    />
                </div>

                <OnboardingNavigationBar
                    onBack={onBack}
                    onNext={handleSave}
                    nextLabel="Spara & fortsätt"
                    nextDisabled={!isValid()}
                    saving={saving}
                />
            </div>
        </>
    );
};

export default CompanyDetailsStep;
